"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";

import { useLanguage } from "../../i18n/language-provider";
import { closeLabel } from "./close-label";
import { cn } from "./utils";
import { HELD_TO_WINDOW, revealFocusThatMovesOn, revealTabThatMovedNothing } from "./held-to-window";
import { Button } from "./button";
import { PHONE_STEP } from "./phone-step";

/**
 * One column, as wide as the dialog and no wider. The dialog's width is fixed,
 * so a column sized by what is in it can only grow past the dialog, and takes
 * every row with it: one word with nowhere to break (an address in a sentence
 * or in a row that truncates, an error's own identifier) laid the rows of four
 * dialogs out up to 115px too wide on a 320px phone, 89px of that past the
 * dialog's own edge and 73px past the window's. Held to the dialog, a caller's
 * `truncate`, `min-w-0` and `break-all` have a width to work against, and a
 * word too long for its line is broken.
 *
 * `break-words` and not `wrap-anywhere`: it breaks a word only where the word
 * would stand past its line, and leaves alone what sizes itself by its longest
 * word (a segmented control, a row of tabs). It is inherited, so it reaches
 * the title, the description and whatever a caller puts inside.
 */
const ONE_COLUMN = "grid-cols-[minmax(0,1fr)] break-words";

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
        className,
      )}
      {...props}
    />
  );
}

/**
 * Held to the window like a confirmation (`HELD_TO_WINDOW`), and for the same
 * reason. Three dialogs had each been given a cap by hand after being found
 * too tall, one of them as `max-h-[90dvh]` with one axis named, so it also
 * panned; one lays its own scroll out; five had none. Measured on 7 Oct 2026
 * over 70 states of the nine dialogs, four skins, two languages and eleven
 * windows, with overlay and with classic scrollbars, 1,536 of 12,320 renders
 * stood past the window: who may reach a property (1,472px of a laptop's
 * 768), temporary access, the password reset link and the channel
 * commissions.
 *
 * Three things a confirmation does not have.
 *
 * The Close button stands in the corner of what scrolls, beside the title, and
 * goes with the title when the dialog is scrolled on. It is the last control
 * in the dialog, so the dialog opens on the caller's first one, and the
 * keyboard that reaches it brings it back into view.
 *
 * The dialog opens on its first control, wherever that is, and Radix puts the
 * focus there without scrolling to it. A control that hands that focus on (a
 * radio group, to its checked item) is scrolled to by the browser, and the
 * dialog opened past its title: so once the opening focus has settled the
 * dialog is put back on its title. A caller that places the opening focus
 * itself (`autoFocus`, or its own `onOpenAutoFocus` that prevents this one) is
 * left where that scrolled it.
 *
 * And a caller lays itself out in more ways. Its own classes come last and
 * win: a title bar and buttons held in view over a body that scrolls, as the
 * auto-sync dialog is built, states `flex flex-col overflow-hidden` and is
 * listed in `dialog.test.tsx`, which reads every caller's source.
 */
function DialogContent({
  className,
  children,
  onFocus,
  onKeyUp,
  onOpenAutoFocus,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content>) {
  const { t } = useLanguage();

  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 sm:max-w-lg",
          HELD_TO_WINDOW,
          ONE_COLUMN,
          className,
        )}
        onOpenAutoFocus={(event) => {
          onOpenAutoFocus?.(event);
          // Read now: an event names what it was dispatched on only while it
          // is being dispatched. Put back a turn later, once the focus Radix
          // is about to place, and any control it is handed on to, has
          // scrolled what it will.
          const dialog = event.currentTarget;
          if (event.defaultPrevented || !(dialog instanceof HTMLElement)) return;
          queueMicrotask(() => {
            dialog.scrollTop = 0;
          });
        }}
        onFocus={(event) => {
          onFocus?.(event);
          revealFocusThatMovesOn(event);
        }}
        onKeyUp={(event) => {
          onKeyUp?.(event);
          revealTabThatMovedNothing(event);
        }}
        {...props}
      >
        {children}
        <DialogPrimitive.Close className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-1 right-1 inline-flex size-10 items-center justify-center rounded-md opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4">
          <XIcon />
          <span className="sr-only">{closeLabel(t)}</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

/**
 * The header's first row ends where the Close button begins.
 *
 * The button stands in the dialog's corner, `top-1 right-1 size-10`, so its
 * box reaches 1.25rem into the dialog's content box: a quarter rem in and two
 * and a half wide, less the dialog's own 1.5rem of padding. The header's
 * first row began at that box's top and ran the whole width, so a title that
 * filled its line ended under the button, where a press on its last letters
 * closed the dialog. Measured on 7 Oct 2026 over 75 states of the eleven
 * dialogs, four skins, two languages and eleven windows, with overlay and
 * with classic scrollbars: a title's letters stood under the button in 2,456
 * of 13,200 renders (1,700 of the 6,000 below `sm`, where the row is centred,
 * and 756 of the 7,200 from `sm` up, for a long property name), under the X
 * itself in 802 of them, by up to 8.7px, and in 243 more the case number
 * beside a title did. Nothing a caller's header draws stands under the button
 * now.
 *
 * The first row is the header's first child: the title, or the row a caller
 * lays the title out in. Three things about the room were measured.
 *
 * The first row's, and not the header's. The description begins under the
 * button's box and stood under it in none of the renders. Narrowed with the
 * title it wrapped a line sooner in 2,123 of the 6,000 below `sm`: the whole
 * header kept clear made 2,804 of them taller, twice what its first row does.
 *
 * The button's whole box, and not its glyph. Half a rem keeps letters out
 * from under the X and leaves them under the button in 1,193 of those 6,000,
 * where a press on them still closes the dialog.
 *
 * Both sides below `sm`, and the right alone from there. Below `sm` the row is
 * centred, and room on the right alone stands a title 10px to the left of the
 * description centred under it. From `sm` up the row starts at the left, and
 * only its end can meet the button.
 *
 * What it costs is a line of that row. 1,842 of the 13,200 are taller (1,416
 * of the 6,000 below `sm`, 426 from `sm` up): 1,579 by a line of the title,
 * 18 to 23px, 243 by a case number that wraps under its title, 26 to 29px,
 * and 20 by more, to 41px (a title two lines longer in 19). The rest stand as
 * they did to a tenth of a pixel, and of the 4,106 that fitted their window
 * whole, 11 scroll now, by 2 to 18px.
 *
 * The room is stated here for the first child, which outranks a padding that
 * child states itself: a title, or the row it shares, states none to its
 * sides. A header that keeps its title clear by hand, in a dialog without
 * padding, takes the room off again where it was put, with `*:first:px-0`
 * (the auto-sync dialog's title bar, `pr-11`). `dialog.test.tsx` reads every
 * caller's source for both, and for a title drawn outside a header or under
 * something else in one, which has no first row to stand in.
 */
const CLEAR_OF_CLOSE = "*:first:px-5 sm:*:first:pl-0";

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-2 text-center sm:text-left", CLEAR_OF_CLOSE, className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className,
      )}
      {...props}
    />
  );
}

/**
 * A button of a dialog's footer: 40px under a finger on a phone, where a
 * label too long for one line wraps (`PHONE_STEP`, the step a confirmation's
 * own buttons take).
 *
 * Every footer drew plain `Button`s at the ordinary `h-9`: 36px beside a
 * phone's 16px root, and 32px for a small one, in every skin but high
 * contrast (measured 7 Oct 2026). The footer does not own its buttons, and a
 * step reached into them from there (`[&>button]:h-10 sm:[&>button]:h-9`)
 * outranks what a button states itself. Measured, it took three such things:
 * the step the auto-sync dialog takes by its own width, a small button's
 * height from `sm` up, and the room a label wrapped onto two lines stands in.
 * So the step is the button's own, and a footer is written with this one.
 * From `sm` up it is the plain button it was.
 *
 * A small button keeps its own height from `sm` up. It has no padding above
 * and below its label, which a set height never needed, so the phone's step
 * brings its own: on two lines a label would fill its button edge to edge.
 *
 * A caller's own classes come last and win, each half on its own.
 * `dialog.footer.test.tsx` reads every footer's source: a plain `Button` in
 * one fails it, unless the footer is listed there with the step it states
 * itself.
 */
function DialogFooterButton({
  className,
  size = "default",
  ...props
}: Omit<React.ComponentProps<typeof Button>, "size"> & {
  size?: "default" | "sm";
}) {
  return (
    <Button
      size={size}
      className={cn(PHONE_STEP, size === "sm" && "py-2 sm:h-8 sm:py-0", className)}
      {...props}
    />
  );
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-lg leading-none font-semibold", className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogFooterButton,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
